import React from 'react';
import { Input } from '@frontdoor/ui/components/input';
import { cn } from '@frontdoor/ui/lib/utils';

interface DateInputProps extends Omit<React.ComponentProps<'input'>, 'onChange' | 'value'> {
  value?: string;
  onChange?: (value: string) => void;
}

export const DateInput = ({ value = '', onChange, className, ...props }: DateInputProps) => {
  const [displayValue, setDisplayValue] = React.useState(value || '');
  const previousDigitCountRef = React.useRef((value || '').replace(/\D/g, '').length);

  React.useEffect(() => {
    setDisplayValue(value || '');
    previousDigitCountRef.current = (value || '').replace(/\D/g, '').length;
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    const currentDigitCount = inputValue.replace(/\D/g, '').length;
    const isAddingDigits = currentDigitCount >= previousDigitCountRef.current;

    previousDigitCountRef.current = currentDigitCount;

    const formatted = formatDateInputValue(inputValue, isAddingDigits);
    setDisplayValue(formatted);
    onChange?.(formatted);
  };

  return (
    <Input
      type="text"
      value={displayValue}
      inputMode="numeric"
      autoComplete="bday"
      onChange={handleChange}
      className={cn(className)}
      placeholder="MM/DD/YYYY"
      maxLength={10}
      {...props}
    />
  );
};

DateInput.displayName = 'DateInput';

function formatDateInputValue(value: string, isAddingDigits: boolean): string {
  const digits = value.replace(/\D/g, '');

  if (digits.length === 0) return '';

  let month = digits.slice(0, 2);
  let day = digits.slice(2, 4);
  let year = digits.slice(4, 8);

  if (month.length === 1 && isAddingDigits && month[0]) {
    const firstDigit = parseInt(month[0], 10);
    if (firstDigit > 1) {
      month = `0${month[0]}`;
    }
  }

  if (month.length === 2) {
    const monthNum = parseInt(month, 10);
    if (monthNum > 12) {
      month = '12';
    } else if (monthNum < 1) {
      month = '01';
    }
  }

  if (day.length === 1 && isAddingDigits && day[0]) {
    const firstDigit = parseInt(day[0], 10);
    if (firstDigit > 3) {
      day = `0${day[0]}`;
    }
  }

  if (day.length === 2) {
    const dayNum = parseInt(day, 10);
    if (dayNum > 31) {
      day = '31';
    } else if (dayNum < 1) {
      day = '01';
    }
  }

  if (year.length > 4) {
    year = year.slice(0, 4);
  }

  if (year.length === 2 && isAddingDigits) {
    const yearNum = parseInt(year, 10);
    if (yearNum < 19) {
      year = `20${year}`;
    }
  }

  let formatted = month;
  if (day) formatted += `/${day}`;
  if (year) formatted += `/${year}`;

  return formatted;
}
